/**
 * useAvailableNodes Composable
 *
 * Provides the list of available node types that can be added to a flow.
 * This is extracted from FlowNodeAdder.vue to be reusable by:
 * - FlowNodeAdder.vue (drag and drop palette)
 * - NodeInsertionPalette.vue (Vimium-style keyboard insertion)
 *
 * Features:
 * - Groups nodes by category (Display, Actions, Flow)
 * - Filters nodes based on flow type (system flow vs regular)
 * - Shows/hides nodes based on integration capabilities
 * - Dynamically shows High Score node when multiple scores exist
 */

import { computed, type ComputedRef } from 'vue'
import { scoresInFlow, variablesInFlow } from '@rightmessage/flow-builder'
import { makeId, idPrefixes } from '@/services/uuid'

// Stores are auto-imported via unplugin-auto-import

export interface NodeOption {
  label: string
  icon?: string
  tooltip?: {
    image?: string
    headline: string
    description: string
  }
  node?: any
  api?: string
  comingsoon?: boolean
  locked?: boolean
  lockedMessage?: string
}

export interface NodeGroup {
  label: string
  options: NodeOption[]
}

export interface UseAvailableNodesOptions {
  /** Flow ID to get available nodes for */
  flowId: string | ComputedRef<string>
}

export function useAvailableNodes(options: UseAvailableNodesOptions) {
  const flowStore = useFlowStore()
  const questionStore = useQuestionStore()
  const integrationStore = useIntegrationStore()
  const { isFeatureLocked } = useFeatureGating()
  const webhookLocked = computed(() => isFeatureLocked('webhooks'))

  // Normalize flowId to a computed
  const flowIdRef = computed(() => {
    return typeof options.flowId === 'string' ? options.flowId : options.flowId.value
  })

  const flow = computed(() => {
    return flowStore.getById(flowIdRef.value)
  })

  /**
   * Available scores in the flow for High Score node visibility
   */
  const flowScores = computed(() => {
    if (!flow.value?.funnelDefinition) return []
    return scoresInFlow(flow.value.funnelDefinition, questionStore.questions)
  })

  /**
   * Available variables across ALL flows for High Score variable mode
   */
  const allFlowVariables = computed(() => {
    const allVariables: string[] = []
    const flows = flowStore.flows || []
    flows.forEach((f: any) => {
      if (f.funnelDefinition) {
        const vars = variablesInFlow(f.funnelDefinition)
        allVariables.push(...vars)
      }
    })
    return [...new Set(allVariables)]
  })

  /**
   * Check if we should show the High Score node option.
   * Requires 2+ scores OR 2+ variables to make a meaningful comparison.
   */
  const hasMultipleScores = computed(() => flowScores.value.length > 1)
  const hasMultipleVariables = computed(() => allFlowVariables.value.length > 1)
  const showHighestScore = computed(() =>
    hasMultipleScores.value || hasMultipleVariables.value
  )

  /**
   * Check integration capabilities
   */
  const hasIntegration = computed(() => !!integrationStore.integratedPartner)

  const supportsTagOperations = computed(() => {
    if (!hasIntegration.value) return false
    const partner = integrationStore.integratedPartner
    return (
      partner?.espFeatureSupport?.syncMethods?.includes('tag') ||
      partner?.espFeatureSupport?.tag
    )
  })

  /**
   * Grouped available nodes based on flow type and integration capabilities
   */
  const availableNodes = computed<NodeGroup[]>(() => {
    const isSystemFlow = flow.value?.isSystemFlow

    const nodeGroups: NodeGroup[] = [
      {
        label: 'Display',
        options: [
          {
            label: 'Question',
            icon: 'hugeicons:message-question',
            tooltip: {
              image: '/img/tooltip/question.png',
              headline: 'Question',
              description:
                'Ask visitors a question to segment them and personalize their experience based on their answers.'
            },
            node: {
              type: 'sg',
              segmentGroupId: null,
              ask: true,
              split: false
            }
          },
          {
            label: 'Form',
            icon: 'hugeicons:inbox',
            tooltip: {
              image: '/img/tooltip/form.png',
              headline: 'Form',
              description:
                'Display a form to collect visitor information like email addresses, names, or custom data.'
            },
            node: {
              type: 'offer',
              desiredType: 'form',
              offerId: null
            }
          },
          {
            label: 'Offer',
            icon: 'hugeicons:gift',
            tooltip: {
              image: '/img/tooltip/offer.png',
              headline: 'Offer / Promo',
              description:
                'Show a promotional offer, announcement, or call-to-action to engage visitors and drive conversions.'
            },
            node: {
              type: 'offer',
              desiredType: 'offer',
              offerId: null
            }
          },
          {
            label: 'Message',
            icon: 'hugeicons:message-01',
            tooltip: {
              image: '/img/tooltip/message.png',
              headline: 'Submit Message',
              description:
                'Display a success or thank you message after a form is submitted.'
            },
            node: {
              type: 'message',
              content: 'Thank you!'
            }
          }
        ]
      },
      {
        label: 'Actions',
        options: isSystemFlow
          ? [
            // System Flow: Individual pre-configured action nodes
            {
              label: 'Set Score',
              icon: 'hugeicons:award-01',
              tooltip: {
                headline: 'Add/Update Score',
                description:
                  'Adjust a lead score based on visitor behavior or actions.'
              },
              node: {
                type: 'data',
                actions: [
                  {
                    id: makeId(idPrefixes.ACTION),
                    type: 'score',
                    score: '',
                    adjust: 1
                  }
                ]
              }
            },
            {
              label: 'Set Variable',
              icon: 'hugeicons:code-circle',
              tooltip: {
                headline: 'Set Variable',
                description:
                  'Store custom data in a variable that can be used throughout the flow or in personalization.'
              },
              node: {
                type: 'data',
                actions: [
                  {
                    id: makeId(idPrefixes.ACTION),
                    type: 'variable',
                    variableName: '',
                    variableValue: '',
                    expiresType: 'never'
                  }
                ]
              }
            },
            {
              label: 'Set Segment',
              icon: 'hugeicons:user-group',
              tooltip: {
                headline: 'Set Segment',
                description:
                  'Assign the visitor to a specific segment within a dimension.'
              },
              node: {
                type: 'data',
                actions: [
                  {
                    id: makeId(idPrefixes.ACTION),
                    type: 'set_segment',
                    segmentGroupId: '',
                    segmentId: ''
                  }
                ]
              }
            },
            hasIntegration.value
              ? {
                label: 'Set Field',
                icon: 'hugeicons:note-edit',
                tooltip: {
                  headline: 'Set Field Value',
                  description:
                    'Update a custom field in your email platform with visitor data or calculated values.'
                },
                node: {
                  type: 'data',
                  actions: [
                    {
                      id: makeId(idPrefixes.ACTION),
                      type: 'field',
                      field: '',
                      value: ''
                    }
                  ]
                }
              }
              : null,
            supportsTagOperations.value
              ? {
                label: 'Add Tag',
                icon: 'hugeicons:bookmark-add-01',
                tooltip: {
                  headline: 'Add Tag',
                  description:
                    'Automatically add a tag to visitors in your email platform.'
                },
                node: {
                  type: 'data',
                  actions: [
                    {
                      id: makeId(idPrefixes.ACTION),
                      type: 'add_tag',
                      tagId: ''
                    }
                  ]
                }
              }
              : null,
            supportsTagOperations.value
              ? {
                label: 'Remove Tag',
                icon: 'hugeicons:bookmark-remove-01',
                tooltip: {
                  headline: 'Remove Tag',
                  description:
                    'Remove a tag from visitors in your email platform.'
                },
                node: {
                  type: 'data',
                  actions: [
                    {
                      id: makeId(idPrefixes.ACTION),
                      type: 'remove_tag',
                      tagId: ''
                    }
                  ]
                }
              }
              : null,
            {
              label: 'Webhook',
              icon: 'hugeicons:webhook',
              tooltip: {
                headline: 'Fire Webhook',
                description:
                  'Send an HTTP request to an external URL when a visitor reaches this point in the flow.'
              },
              locked: webhookLocked.value,
              lockedMessage: 'Webhooks are available on the Pro plan.',
              node: {
                type: 'webhook',
                url: '',
                method: 'POST',
                customPayload: '',
                fireOnce: true,
                timeoutMs: 10000
              }
            },
            {
              label: 'Redirect',
              icon: 'hugeicons:move-right',
              tooltip: {
                headline: 'Redirect',
                description: 'Send visitors to another page or external website.'
              },
              node: { type: 'redirect', url: '', open: false, sendSubscriberData: false }
            },
            {
              label: 'Code',
              icon: 'hugeicons:code',
              tooltip: {
                headline: 'Code',
                description: 'Execute custom JavaScript code.'
              },
              node: { type: 'code', name: '', code: '' }
            }
          ].filter(Boolean) as NodeOption[]
          : [
            // Regular Flow: Keep existing structure
            {
              label: 'Redirect',
              icon: 'hugeicons:move-right',
              tooltip: {
                image: '/img/tooltip/redirect.png',
                headline: 'Redirect',
                description:
                  'Send visitors to another page or external website, perfect for routing to specific landing pages.'
              },
              node: { type: 'redirect', url: '', open: false, sendSubscriberData: false }
            },
            {
              label: 'Code',
              icon: 'hugeicons:code',
              tooltip: {
                image: '/img/tooltip/code.png',
                headline: 'Code',
                description: 'Execute custom JavaScript code on your website.'
              },
              node: { type: 'code', name: '', code: '' }
            },
            {
              label: 'Set Data',
              icon: 'hugeicons:database',
              tooltip: {
                image: '/img/tooltip/data.png',
                headline: 'Set Data',
                description:
                  'Store data like lead scores, variables, or updating known subscriber data.'
              },
              node: {
                type: 'data',
                actions: [
                  {
                    id: makeId(idPrefixes.ACTION),
                    type: 'score',
                    score: '',
                    adjust: 1
                  }
                ]
              }
            },
            {
              label: 'Webhook',
              icon: 'hugeicons:webhook',
              tooltip: {
                headline: 'Fire Webhook',
                description:
                  'Send an HTTP request to an external URL when a visitor reaches this point in the flow.'
              },
              locked: webhookLocked.value,
              lockedMessage: 'Webhooks are available on the Pro plan.',
              node: {
                type: 'webhook',
                url: '',
                method: 'POST',
                customPayload: '',
                fireOnce: true,
                timeoutMs: 10000
              }
            }
          ]
      },
      {
        label: 'Flow',
        options: [
          {
            label: 'Condition',
            icon: 'hugeicons:filter-horizontal',
            tooltip: {
              image: '/img/tooltip/condition.png',
              headline: 'Condition',
              description:
                'Branch the flow based on visitor data, behavior, or previous responses to create personalized paths.'
            },
            node: {
              type: 'yesno',
              condition: { or: [] },
              no: [],
              yes: []
            }
          },
          {
            label: 'Router',
            icon: 'hugeicons:flow-square',
            tooltip: {
              image: '/img/tooltip/router.png',
              headline: 'Router',
              description:
                'Route visitors based on their current segmentation within a Segment Group.'
            },
            node: {
              type: 'sg',
              ask: false,
              split: [],
              segmentGroupId: null
            }
          },
          {
            label: 'Split',
            icon: 'hugeicons:hierarchy-square-08',
            tooltip: {
              image: '/img/tooltip/random.png',
              headline: 'Random Split',
              description:
                'Randomly split visitors between different paths to test offers and content equally.'
            },
            node: { type: 'random', split: [{ child: [] }, { child: [] }] }
          },
          showHighestScore.value
            ? {
              label: 'High Score',
              icon: 'hugeicons:award-01',
              tooltip: {
                image: '/img/tooltip/highscore.png',
                headline: 'High Score',
                description:
                  'Route visitors based on their highest lead score or variable value.'
              },
              node: {
                type: 'highscore',
                mode: hasMultipleScores.value ? 'scores' : 'variables',
                split: hasMultipleScores.value
                  ? flowScores.value.map((score: string) => ({
                    score,
                    child: []
                  }))
                  : []
              }
            }
            : null,
          {
            label: 'A/B Test',
            tooltip: {
              image: '/img/tooltip/abtest.png',
              headline: 'A/B Test',
              description:
                'Split test different flow variations to optimize conversion rates and user experience.'
            },
            comingsoon: true
          },
          {
            label: 'Exit Flow',
            icon: 'hugeicons:logout-04',
            tooltip: {
              image: '/img/tooltip/exit.png',
              headline: 'Exit Flow',
              description:
                'End the flow, which will cause the widget running this Flow to be dismissed.'
            },
            node: { type: 'exit' }
          }
        ].filter(Boolean) as NodeOption[]
      }
    ]

    // Filter out Display nodes if this is a System Flow
    // System flows run in the background without UI, so Display nodes are not allowed
    if (isSystemFlow) {
      return nodeGroups.filter((group) => group.label !== 'Display')
    }

    return nodeGroups
  })

  /**
   * Flat list of all available nodes (for search/filtering)
   */
  const flatNodes = computed(() => {
    return availableNodes.value.flatMap((group) =>
      group.options
        .filter((opt) => !opt.comingsoon && opt.node)
        .map((opt) => ({
          ...opt,
          group: group.label
        }))
    )
  })

  return {
    flow,
    availableNodes,
    flatNodes,
    flowScores,
    allFlowVariables,
    hasMultipleScores,
    hasMultipleVariables,
    showHighestScore,
    hasIntegration,
    supportsTagOperations
  }
}
